import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChatGroup } from '@/types/chat';
import { unifiedChatService } from '@/services/chat/unifiedChatService';
import ThreadedMessage from './ThreadedMessage';
import { ChatMessage } from '@/types/chat';

interface MessagesListProps {
  messages: ChatMessage[];
  selectedGroup: ChatGroup;
  currentUsername?: string;
  currentUserId?: string;
  chatType?: 'open' | 'read_only';
  isCreator?: boolean;
  isMobile?: boolean;
  highlightMessageId?: string;
  onReplyClick?: (messageId: string, message: ChatMessage) => void;
}

const MessagesList = ({
  messages,
  selectedGroup,
  currentUsername,
  currentUserId,
  isCreator = false,
  highlightMessageId,
  onReplyClick
}: MessagesListProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [threadReplies, setThreadReplies] = useState<Map<string, ChatMessage[]>>(new Map());
  const [loadingThreads, setLoadingThreads] = useState<Set<string>>(new Set());

  console.log('=== MESSAGES LIST RENDER (THREADED) ===');
  console.log('Root messages count:', messages?.length || 0);
  console.log('Current username:', currentUsername);
  console.log('Expanded threads:', Array.from(expandedThreads));

  // Load thread replies when expanding
  const loadThreadReplies = useCallback(async (parentMessageId: string) => {
    if (loadingThreads.has(parentMessageId)) {
      console.log('Already loading thread:', parentMessageId);
      return;
    }

    console.log('Loading thread replies for:', parentMessageId);
    setLoadingThreads(prev => new Set(prev).add(parentMessageId));

    try {
      const replies = await unifiedChatService.getThreadReplies(parentMessageId);
      console.log(`Loaded ${replies.length} replies for thread`);
      
      setThreadReplies(prev => {
        const newMap = new Map(prev);
        newMap.set(parentMessageId, replies);
        return newMap;
      });
    } catch (error) {
      console.error('Error loading thread replies:', error);
    } finally {
      setLoadingThreads(prev => {
        const newSet = new Set(prev);
        newSet.delete(parentMessageId);
        return newSet;
      });
    }
  }, [loadingThreads]);

  // Toggle thread expansion
  const handleToggleThread = useCallback(async (messageId: string) => {
    console.log('Toggling thread:', messageId);
    
    const isExpanded = expandedThreads.has(messageId);
    
    if (isExpanded) {
      // Collapse thread
      setExpandedThreads(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    } else {
      // Expand thread and load replies if not already loaded
      setExpandedThreads(prev => new Set(prev).add(messageId));
      
      if (!threadReplies.has(messageId)) {
        await loadThreadReplies(messageId);
      }
    }
  }, [expandedThreads, threadReplies, loadThreadReplies]);

  // Handle reply button click
  const handleReplyClick = useCallback((messageId: string, message: ChatMessage) => {
    console.log('Reply button clicked for message:', messageId);
    onReplyClick?.(messageId, message);
  }, [onReplyClick]);

  // Scroll to highlighted message
  useEffect(() => {
    if (highlightMessageId && scrollAreaRef.current) {
      console.log('Scrolling to highlighted message:', highlightMessageId);
      const messageElement = document.querySelector(`[data-message-id="${highlightMessageId}"]`);
      
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightMessageId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current && !highlightMessageId) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, highlightMessageId]);

  // Listen for new replies and update expanded threads
  useEffect(() => {
    const handleReplyReceived = async (event: CustomEvent) => {
      const newMessage = event.detail as ChatMessage;
      
      if (!newMessage.parent_message_id) return;
      
      console.log('📥 New reply received:', {
        messageId: newMessage.message_id,
        parentId: newMessage.parent_message_id,
        fromCurrentUser: newMessage.user_id === currentUserId,
        isThreadExpanded: expandedThreads.has(newMessage.parent_message_id)
      });
      
      // Update parent message reply count in the messages list
      const parentMessage = messages.find(m => m.message_id === newMessage.parent_message_id);
      if (parentMessage) {
        console.log('📊 Updating reply count for parent message');
        // This will trigger a re-render with updated count
        window.dispatchEvent(new CustomEvent('reply-count-updated', { 
          detail: { 
            messageId: newMessage.parent_message_id,
            newCount: (parentMessage.reply_count || 0) + 1
          } 
        }));
      }
      
      // If current user sent this reply, auto-expand the thread
      const isFromCurrentUser = newMessage.user_id === currentUserId;
      if (isFromCurrentUser) {
        console.log('🔓 Auto-expanding thread for current user reply');
        setExpandedThreads(prev => new Set(prev).add(newMessage.parent_message_id!));
        
        // Load thread replies if not already loaded
        if (!threadReplies.has(newMessage.parent_message_id!)) {
          await loadThreadReplies(newMessage.parent_message_id!);
        }
      }
      
      // Add reply to threadReplies if thread is expanded (or just became expanded)
      if (expandedThreads.has(newMessage.parent_message_id) || isFromCurrentUser) {
        setThreadReplies(prev => {
          const newMap = new Map(prev);
          const existingReplies = newMap.get(newMessage.parent_message_id!) || [];
          
          // Check for duplicates
          if (existingReplies.some(r => r.message_id === newMessage.message_id)) {
            console.log('⚠️ Reply already exists, skipping duplicate');
            return prev;
          }
          
          console.log('✅ Adding reply to thread replies');
          newMap.set(newMessage.parent_message_id!, [...existingReplies, newMessage]);
          return newMap;
        });
      }
    };
    
    window.addEventListener('chat-reply-received', handleReplyReceived as EventListener);
    
    return () => {
      window.removeEventListener('chat-reply-received', handleReplyReceived as EventListener);
    };
  }, [expandedThreads, messages, currentUserId, threadReplies, loadThreadReplies]);

  const getDisplayName = (message: ChatMessage) => {
    return message.user_profile?.username || 
           message.user_profile?.first_name || 
           'Unknown User';
  };

  const isGroupCreator = (message: ChatMessage) => {
    return message.user_id === selectedGroup?.created_by;
  };

  if (!messages || messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-muted-foreground text-sm">
          No messages yet. Be the first to send a message!
        </p>
      </div>
    );
  }

  return (
    <div 
      ref={scrollAreaRef}
      className="h-full overflow-y-auto px-2 py-4 space-y-0"
    >
      {messages.map((message) => {
        const isCurrentUser = currentUsername && 
          getDisplayName(message).toLowerCase() === currentUsername.toLowerCase();
        const isCreatorMessage = isGroupCreator(message);
        const isHighlighted = highlightMessageId === message.message_id;
        const isExpanded = expandedThreads.has(message.message_id);
        const replies = threadReplies.get(message.message_id) || [];

        return (
          <div
            key={message.message_id}
            data-message-id={message.message_id}
          >
            <ThreadedMessage
              message={message}
              isExpanded={isExpanded}
              threadReplies={replies}
              onToggleThread={() => handleToggleThread(message.message_id)}
              onReplyClick={() => handleReplyClick(message.message_id, message)}
              currentUsername={currentUsername}
              isCurrentUser={isCurrentUser}
              isCreator={isCreatorMessage}
              isHighlighted={isHighlighted}
            />
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessagesList;
